/**
 * Template Save Dialog
 * Allows users to save their current email design as a reusable template
 *
 * @file src/components/email/templates/TemplateSaveDialog.tsx
 * @created 2025-10-22
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import EmailService from '@/api/email';
import { Loader2, Save, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface TemplateSaveDialogProps {
  open: boolean;
  onClose: () => void;
  designJson: Record<string, any>;
  templateContentHtml: string;
  organisationId: string;
  restaurantId?: string;
  onSaveSuccess?: (templateId: string) => void;
}

const TEMPLATE_CATEGORIES = [
  { value: 'promotional', label: 'Promotional' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'trigger', label: 'Automated/Trigger' },
  { value: 'other', label: 'Other' },
];

/**
 * TemplateSaveDialog Component
 *
 * Displays a dialog for saving the current email design as a template.
 * Captures template name, description, and category.
 *
 * @example
 * <TemplateSaveDialog
 *   open={showSaveDialog}
 *   onClose={() => setShowSaveDialog(false)}
 *   designJson={editor.getProjectData()}
 *   templateContentHtml={editor.getHtml()}
 *   organisationId="org-123"
 *   onSaveSuccess={(templateId) => console.log('Saved:', templateId)}
 * />
 */
const TemplateSaveDialog: React.FC<TemplateSaveDialogProps> = ({
  open,
  onClose,
  designJson,
  templateContentHtml,
  organisationId,
  restaurantId,
  onSaveSuccess
}) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    templateName: '',
    description: '',
    category: 'other',
    subject: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null); // Clear error on input
  };

  const validateForm = (): boolean => {
    if (!formData.templateName.trim()) {
      setError('Template name is required');
      return false;
    }

    if (formData.templateName.length > 100) {
      setError('Template name must be 100 characters or less');
      return false;
    }

    if (!formData.subject.trim()) {
      setError('Subject line is required');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      console.log('[TemplateSaveDialog] Saving template:', formData.templateName);

      const response = await EmailService.createTemplate({
        template_name: formData.templateName.trim(),
        template_subject: formData.subject.trim(),
        template_content_html: templateContentHtml,
        design_json: designJson,
        template_description: formData.description.trim() || undefined,
        category: formData.category,
        organisation_id: organisationId,
        restaurant_id: restaurantId || undefined,
        is_system_template: false,
        is_active: true
      });

      console.log('[TemplateSaveDialog] Template saved successfully:', response.id);

      toast({
        title: 'Template Saved',
        description: `"${formData.templateName}" has been saved to your templates.`,
      });

      // Reset form
      setFormData({
        templateName: '',
        description: '',
        category: 'other',
        subject: ''
      });

      // Notify parent
      if (onSaveSuccess && response.id) {
        onSaveSuccess(response.id);
      }

      // Close dialog
      onClose();

    } catch (err: any) {
      console.error('[TemplateSaveDialog] Error saving template:', err);
      setError(err.message || 'Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      templateName: '',
      description: '',
      category: 'other',
      subject: ''
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Save className="h-5 w-5" />
            Save as Template
          </DialogTitle>
          <DialogDescription>
            Save your current email design as a reusable template
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" style={{ stroke: 'white', color: 'white' }} />
            <AlertDescription className="!text-white">{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4 py-4">
          {/* Template Name */}
          <div className="space-y-2">
            <Label htmlFor="template-name" className="required">
              Template Name
            </Label>
            <Input
              id="template-name"
              placeholder="e.g., Monthly Newsletter"
              value={formData.templateName}
              onChange={(e) => handleInputChange('templateName', e.target.value)}
              maxLength={100}
              disabled={saving}
              autoFocus
            />
            <p className="text-xs text-gray-500">
              {formData.templateName.length}/100 characters
            </p>
          </div>

          {/* Subject Line */}
          <div className="space-y-2">
            <Label htmlFor="template-subject" className="required">
              Default Subject Line
            </Label>
            <Input
              id="template-subject"
              placeholder="e.g., Check out our latest offers!"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              disabled={saving}
            />
            <p className="text-xs text-gray-500">
              You can change this when creating a campaign
            </p>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="template-category">Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => handleInputChange('category', value)}
              disabled={saving}
            >
              <SelectTrigger id="template-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="template-description">
              Description <span className="text-gray-500 font-normal">(Optional)</span>
            </Label>
            <Textarea
              id="template-description"
              placeholder="Brief description of when to use this template..."
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              maxLength={500}
              disabled={saving}
            />
            <p className="text-xs text-gray-500">
              {formData.description.length}/500 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formData.templateName.trim() || !formData.subject.trim()}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Template
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSaveDialog;
